/**
 * Reusable picker for assigning items to heirs.
 * Used in Vault/Messages/Capsule detail modals.
 */
import { useEffect, useState } from "react";
import { Users, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";

export default function HeirAssignmentPicker({ value, onChange, label }) {
  const { t } = useI18n();
  const [heirs, setHeirs] = useState([]);
  const selected = value || [];
  const resolvedLabel = label || t("hap_default_label");

  useEffect(() => {
    api.get("/heirs").then((r) => setHeirs(r.data)).catch(() => setHeirs([]));
  }, []);

  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((x) => x !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  if (heirs.length === 0) {
    return (
      <div className="text-xs text-faded">
        <span className="font-label">{resolvedLabel}</span>
        <p className="mt-2 italic">{t("hap_no_heirs_yet")}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="font-label text-faded mb-2 flex items-center gap-2">
        <Users className="w-3 h-3 text-gold" /> {resolvedLabel}
      </div>
      <div className="flex flex-wrap gap-2">
        {heirs.map((h) => {
          const on = selected.includes(h.heir_id);
          return (
            <button
              key={h.heir_id}
              type="button"
              onClick={() => toggle(h.heir_id)}
              className={`text-xs px-3 py-2 rounded-md border transition flex items-center gap-2 ${
                on
                  ? "bg-gold/15 border-gold text-gold-light"
                  : "border-gold-subtle text-muted hover:border-gold-strong"
              }`}
              data-testid={`heir-pick-${h.heir_id}`}
            >
              {on && <Check className="w-3 h-3" />}
              {h.name}
              <span className="text-faded">· {h.relationship}</span>
            </button>
          );
        })}
      </div>
      {selected.length === 0 && (
        <p className="text-xs text-faded mt-2 italic">{t("hap_no_assignment_warning")}</p>
      )}
    </div>
  );
}
